import React, { useEffect, useMemo, useState, useCallback, useRef } from 'react';
import { useLanguage } from '../../contexts/LanguageContext';
import useShipmentDelivery from './hooks/useShipmentDelivery';
import ShipmentDeliveryList from './components/ShipmentDeliveryList';
import ShipmentDeliveryFilter from './components/ShipmentDeliveryFilter';
import { createInitialFilters, convertFiltersToPrimeReactFormat } from '../../utils/filterUtils';
import { FilterMatchMode } from 'primereact/api';
import { Button } from 'primereact/button';
import { useLazyExportShipmentDeliveryToExcelQuery } from '../../api/endpoints/shipmentDelivery';
import { toastService } from '../../utils/toastService';
import DocumentDownloadService from '../../utils/documentDownloadService';
import { formatDateForApi, getYesterdayDate } from '../../utils/dateUtils';
import { useGetLookUpDropdownListQuery } from '../../api';
import warehouseMapping from '../../utils/warehouseMapping';

const ShipmentDeliveryPage = () => {
    const { th, getCurrentLanguageId } = useLanguage();
    const downloadService = useRef(new DocumentDownloadService());

    const [exportLoading, setExportLoading] = useState(false);

    const languageId = useMemo(() => getCurrentLanguageId(), [getCurrentLanguageId]);

    const { items, totalRecords, isLoading, lazyParams, sortField, sortOrder, activeFilters, dateRange, filterUiKey, exportParams, onPage, onSort, onFiltersChange, initFilters, setDateRange } = useShipmentDelivery();

    const { data: priorityLookup = [] } = useGetLookUpDropdownListQuery({
        LanguageId: languageId,
        GroupCode: 'YP_ORDER_PRIORITY'
    });

    const orderPriorityOptions = useMemo(() => {
        const list = Array.isArray(priorityLookup) ? priorityLookup : [];
        const byLabel = new Map();
        list.forEach((x) => {
            const label = x.translatedText;
            if (!label) return;
            if (!byLabel.has(label)) byLabel.set(label, []);
            byLabel.get(label).push(String(x.code));
        });
        return Array.from(byLabel.entries()).map(([label, codes]) => ({
            label,
            value: codes.join(',')
        }));
    }, [priorityLookup]);

    const warehouseOptions = useMemo(() => Object.entries(warehouseMapping.WAREHOUSE_MAP).map(([value, label]) => ({ label, value })), []);

    const filterFields = useMemo(
        () => [
            { field: 'startDate', headerName: th('shipmentDelivery.startDate'), type: 'date' },
            { field: 'endDate', headerName: th('shipmentDelivery.endDate'), type: 'date' }
        ],
        [th]
    );

    const handleApplyFilters = useCallback(
        (updated) => {
            setDateRange({
                startDate: updated?.startDate ? formatDateForApi(updated.startDate) : null,
                endDate: updated?.endDate ? formatDateForApi(updated.endDate) : null
            });
        },
        [setDateRange]
    );

    const [exportQuery] = useLazyExportShipmentDeliveryToExcelQuery();

    const handleExport = useCallback(async () => {
        try {
            setExportLoading(true);
            const blob = await exportQuery({
                ...exportParams,
                startDate: exportParams?.startDate || dateRange?.startDate || formatDateForApi(getYesterdayDate())
            }).unwrap();
            if (!blob || !(blob instanceof Blob)) throw new Error('Invalid blob response');

            const timestamp = new Date().toISOString().replace(/[-:]/g, '').split('.')[0];
            downloadService.current.downloadBlob(blob, `SevkiyatlarVeTeslimatlar_${timestamp}.xlsx`, true);
            toastService?.success?.(th('shipmentDelivery.exportSuccess'));
        } catch (error) {
            console.error('Export error:', error);
            toastService?.error?.(error?.data?.errorMessage || error?.message || th('shipmentDelivery.exportError'));
        } finally {
            setExportLoading(false);
        }
    }, [exportQuery, exportParams, dateRange, th]);

    return (
        <div className="card">
            <div className="flex justify-content-between align-items-center mb-4">
                <h1 className="m-0">{th('shipmentDelivery.title') || 'Shipments and Deliveries Report'}</h1>
                <div className="flex gap-2">
                    <Button icon="pi pi-filter-slash" className="p-button-outlined p-button-secondary" onClick={initFilters} tooltip={th('filter.reset')} tooltipOptions={{ position: 'left' }} />
                    <Button label={th('shipmentDelivery.excelExport')} icon="pi pi-file-excel" className="p-button-success" onClick={handleExport} loading={exportLoading} disabled={exportLoading} />
                </div>
            </div>

            <div className="mb-2">
                <ShipmentDeliveryFilter filterFields={filterFields} dateRange={dateRange} filterUiKey={filterUiKey} handleApplyFilters={handleApplyFilters} handleResetFilters={initFilters} />
            </div>

            <ShipmentDeliveryList
                data={items}
                totalRecords={totalRecords}
                isLoading={isLoading}
                onPage={onPage}
                onSort={onSort}
                lazyParams={lazyParams}
                sortField={sortField}
                sortOrder={sortOrder}
                activeFilters={activeFilters}
                onFiltersChange={onFiltersChange}
                onResetFilters={initFilters}
                warehouseOptions={warehouseOptions}
                orderPriorityOptions={orderPriorityOptions}
            />
        </div>
    );
};

export default ShipmentDeliveryPage;
